import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./hooks";
import { RootState } from "../redux/store";
import {
    minimalToast,
    NotificationCategory,
    NotificationPriority,
    NotificationSeverity,
} from "../utils/toast";

type OrganizationFeatures = NonNullable<
    NonNullable<RootState["organization"]["selectedOrganization"]>["features"]
>;

// Feature restrictions mapping - maps route prefixes to the organization
// feature flag that must be on for the route to open.
export const FEATURE_RESTRICTED_ROUTES: Record<string, keyof OrganizationFeatures> = {
    "/image-verification": "imageVerification",
    "/ai-audits": "aiAudits",
    "/assessments": "assessments",
    "/one-time-audits": "oneTimeAudits",
    "/anecdotal-records": "anecdotalRecords",
};

// How long to wait for the selected organization to hydrate before treating
// "no organization" as "no feature". Matches the sidebar's own hydration
// timeout so both give up at the same moment.
export const ORG_HYDRATION_TIMEOUT_MS = 6000;

export const findRestrictedRoute = (pathname: string): string | undefined =>
    Object.keys(FEATURE_RESTRICTED_ROUTES).find((route) =>
        pathname.startsWith(route)
    );

/**
 * Centralized feature access check. Sends the user Home when the current
 * route needs an organization feature the selected organization lacks.
 *
 * On a hard refresh `selectedOrganization` is null until the Navbar restores
 * it from storage after the profile loads, so the check is deferred until
 * the organization is known (or the wait times out). Deciding on the empty
 * store used to bounce every refresh of a restricted page to Home with an
 * "Access Denied" toast.
 */
export const useFeatureAccessControl = (): void => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const selectedOrganization = useAppSelector(
        (state: RootState) => state.organization.selectedOrganization
    );
    const [orgWaitTimedOut, setOrgWaitTimedOut] = useState(false);

    useEffect(() => {
        if (selectedOrganization) {
            return;
        }
        const timer = setTimeout(
            () => setOrgWaitTimedOut(true),
            ORG_HYDRATION_TIMEOUT_MS
        );
        return () => clearTimeout(timer);
    }, [selectedOrganization]);

    useEffect(() => {
        const restrictedRoute = findRestrictedRoute(location.pathname);
        if (!restrictedRoute) {
            return;
        }
        // Still hydrating: nothing to decide yet.
        if (!selectedOrganization && !orgWaitTimedOut) {
            return;
        }

        const requiredFeature = FEATURE_RESTRICTED_ROUTES[restrictedRoute];
        const hasFeature =
            selectedOrganization?.features?.[requiredFeature] ?? false;

        if (!hasFeature) {
            minimalToast({
                dispatch,
                title: "Access Denied",
                message: `You don't have access to this feature. Contact your P3M Super Admin for more information.`,
                type: NotificationSeverity.WARNING,
                category: NotificationCategory.ALERT,
                priority: NotificationPriority.HIGH,
            });
            navigate("/", { replace: true });
        }
    }, [
        location.pathname,
        selectedOrganization,
        orgWaitTimedOut,
        dispatch,
        navigate,
    ]);
};

export default useFeatureAccessControl;
